import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

// Held CDM/OCF/PP drafts (KIN-1236). A review registered mid-month or during a
// GP panel transfer is saved by the BE as a DRAFT (not sent) until the 1st of
// the following month; these hooks list those drafts and drive the FE-initiated
// release once PCRS can verify the panel. Contract: KIN-1234 / KIN-1235.
//
// Additive and inert until the BE hold feature is live (KIN-1270 persistence +
// the `cdm.midmonth.hold.enable` flag): with nothing held, the list is empty and
// release is a no-op.

// Lists the tenant's held drafts. Mirrors useSavePcrsSchemeCheck conventions
// (tenant from localStorage, axios instance per call, skipGlobalError).
export const useCdmDrafts = ({ enabled = true } = {}) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["cdmDrafts", tenantVal],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`${tenantVal}/cdm/drafts`);

      return response.data ?? [];
    },
    enabled,
  });
};

// Re-runs PCRS per drafted patient (the caller supplies the fresh scheme-check)
// and asks the BE to release verified drafts. Body: a list of
// { patientId, schemeCheck: { schemeStartDate, doctorNum, validPatient,
// invalidReason } }. Returns per-item outcomes (RELEASED / STILL_HELD /
// REJECTED / NOT_FOUND / ERROR).
export const useReleaseCdmDrafts = () => {
  const queryClient = useQueryClient();
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (items) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/cdm/drafts/release`,
        items
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cdmDrafts", tenantVal] });
      queryClient.invalidateQueries({ queryKey: ["patientCDMStatus"] });
      queryClient.invalidateQueries({ queryKey: ["patientCDM"] });
    },
  });
};
